// src/scenes/NameBuilderScene.js
// ─────────────────────────────────────────────────────────────────────────────
// Step-by-step in-game username builder. Curated word pools = no profanity.
// Shows on first level launch. Name grows as player unlocks more slots.
// ─────────────────────────────────────────────────────────────────────────────

import Phaser from 'phaser';
import { NAME_SLOTS, buildDisplayName, buildLeaderboardName } from '../data/nameWords.js';
import { LevelStore, REGIONS_ORDER } from '../game/levelStore.js';
import { getCountryCode, toFlag } from '../services/geo.js';
import { I18n } from '../game/I18n.js';
import { saveIdentity, recoverIdentity, getPlayerId } from '../services/identity.js';
import bgImg from '../assets/backgrounds/main_screen_background.png';

// Creature face stickers for preview
import faceFogo     from '../assets/monsters/faces/fogo_face.png';
import faceRaio     from '../assets/monsters/faces/raio_face.png';
import faceJato     from '../assets/monsters/faces/jato_face.png';
import faceTerra    from '../assets/monsters/faces/terra_face.png';
import faceControlo from '../assets/monsters/faces/controlo_face.png';
import faceNoxys    from '../assets/monsters/faces/noxys_face.png';

const CREATURE_FACES = {
  'Pyron':   { key: 'nb-face-fogo',     src: faceFogo },
  'Voltrix': { key: 'nb-face-raio',     src: faceRaio },
  'Aquyra':  { key: 'nb-face-jato',     src: faceJato },
  'Sylrana': { key: 'nb-face-terra',    src: faceTerra },
  'Athena':  { key: 'nb-face-controlo', src: faceControlo },
  'Noxys':   { key: 'nb-face-noxys',    src: faceNoxys },
};

const GOLD   = 0xfbbf24;
const BG     = 0x05080f;
const CARD   = 0x0d1117;
const SUBTLE = 0x1e293b;
const D      = 5;

const STORAGE_KEY     = 'pzm_identity';
const DISCORD_KEY     = 'pzm_discord';

export default class NameBuilderScene extends Phaser.Scene {
  constructor() { super('NameBuilderScene'); }

  init(data) {
    // Where to go after name is created
    this.nextScene = data.nextScene || 'LevelScene';
    this.nextData  = data.nextData  || {};
    this.editMode  = data.editMode  || false;
  }

  preload() {
    if (!this.textures.exists('name-bg')) {
      this.load.image('name-bg', bgImg);
    }
    for (const { key, src } of Object.values(CREATURE_FACES)) {
      if (!this.textures.exists(key)) {
        this.load.image(key, src);
      }
    }
  }

  create() {
    const W = this.scale.width;
    const H = this.scale.height;
    this.W = W;
    this.H = H;

    // Background
    this.add.rectangle(W / 2, H / 2, W, H, BG, 1);

    // Fetch country in background
    this._flag = '🌐';
    this._countryCode = 'XX';
    getCountryCode().then(code => {
      this._countryCode = code;
      this._flag = toFlag(code);
      this._updatePreview();
    });

    // Load existing identity if editing
    const existing = this._loadIdentity();

    // ── State ──────────────────────────────────────────────────────────
    this._selections = existing?.selections || {};
    this._year = existing?.year || '';
    this._availableSlots = this._getAvailableSlots();

    // ── Header ─────────────────────────────────────────────────────────
    this.add.text(W / 2, 36, this.editMode ? I18n.t('name_edit_title') || 'EDIT IDENTITY' : I18n.t('name_create_title') || 'CREATE YOUR IDENTITY', {
      fontFamily: 'Osaka, system-ui, Arial', fontSize: '18px',
      color: '#fbbf24', stroke: '#0b0f14', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(D);

    this.add.text(W / 2, 62, I18n.t('name_subtitle') || 'Build your Pazurumon name', {
      fontFamily: 'system-ui, Arial', fontSize: '12px', color: '#64748b',
    }).setOrigin(0.5).setDepth(D);

    // ── Preview area ───────────────────────────────────────────────────
    this.add.rectangle(W / 2, 108, W - 32, 52, CARD, 0.95)
      .setStrokeStyle(1, GOLD, 0.4).setDepth(D);

    this._previewText = this.add.text(W / 2, 108, '', {
      fontFamily: 'Osaka, system-ui, Arial', fontSize: '15px',
      color: '#e2e8f0', wordWrap: { width: W - 60 },
    }).setOrigin(0.5).setDepth(D + 1);

    // ── Slot pickers ───────────────────────────────────────────────────
    this._slotObjects = [];
    this._buildSlots();

    // ── Year input ─────────────────────────────────────────────────────
    this._buildYearPicker();

    // ── Randomize button ───────────────────────────────────────────────
    const rndY = this._nextY + 8;
    const rndBtn = this.add.rectangle(W / 2, rndY, 180, 34, SUBTLE, 0.9)
      .setStrokeStyle(1, 0x64748b, 0.5).setDepth(D)
      .setInteractive({ useHandCursor: true });
    this.add.text(W / 2, rndY, I18n.t('name_randomize') || '🎲  RANDOMIZE', {
      fontFamily: 'system-ui, Arial', fontSize: '12px', color: '#94a3b8', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(D + 1);
    rndBtn.on('pointerover', () => rndBtn.setFillStyle(0x334155, 0.9));
    rndBtn.on('pointerout', () => rndBtn.setFillStyle(SUBTLE, 0.9));
    rndBtn.on('pointerdown', () => this._randomize());

    // ── Discord (optional) ─────────────────────────────────────────────
    const discY = rndY + 42;
    this.add.text(W / 2, discY, I18n.t('name_discord_label') || 'Discord (optional)', {
      fontFamily: 'system-ui, Arial', fontSize: '11px', color: '#475569',
    }).setOrigin(0.5).setDepth(D);

    this._discordInput = null;
    this._discordValue = existing?.discord || localStorage.getItem(DISCORD_KEY) || '';

    const discBtnY = discY + 22;
    const discBtn = this.add.rectangle(W / 2, discBtnY, 220, 30, SUBTLE, 0.8)
      .setStrokeStyle(1, 0x5865f2, 0.5).setDepth(D)
      .setInteractive({ useHandCursor: true });
    this._discordLabel = this.add.text(W / 2, discBtnY,
      this._discordValue || (I18n.t('name_discord_tap') || 'tap to add @username'), {
        fontFamily: 'system-ui, Arial', fontSize: '12px',
        color: this._discordValue ? '#7289da' : '#334155',
      }).setOrigin(0.5).setDepth(D + 1);
    discBtn.on('pointerdown', () => this._promptDiscord());

    // ── Confirm button ─────────────────────────────────────────────────
    const btnY = H - 100;
    const confirmBtn = this.add.rectangle(W / 2, btnY, 260, 46, 0x0f2a4a, 0.95)
      .setStrokeStyle(2, GOLD, 0.8).setDepth(D)
      .setInteractive({ useHandCursor: true });
    this.add.text(W / 2, btnY, this.editMode
      ? (I18n.t('name_save') || '✓  SAVE IDENTITY')
      : (I18n.t('name_confirm') || '✓  CREATE IDENTITY'), {
      fontFamily: 'Osaka, system-ui, Arial', fontSize: '15px',
      color: '#fbbf24',
    }).setOrigin(0.5).setDepth(D + 1);
    confirmBtn.on('pointerover', () => confirmBtn.setFillStyle(0x1e3a5f, 0.95));
    confirmBtn.on('pointerout', () => confirmBtn.setFillStyle(0x0f2a4a, 0.95));
    confirmBtn.on('pointerdown', () => this._confirm());

    // ── Skip + Recover (only on first time, not edit) ─────────────────
    if (!this.editMode) {
      const skipBtn = this.add.text(W / 2 - 70, H - 55, I18n.t('name_skip') || 'skip for now', {
        fontFamily: 'system-ui, Arial', fontSize: '12px', color: '#334155',
      }).setOrigin(0.5).setDepth(D).setInteractive({ useHandCursor: true });
      skipBtn.on('pointerover', () => skipBtn.setColor('#64748b'));
      skipBtn.on('pointerout', () => skipBtn.setColor('#334155'));
      skipBtn.on('pointerdown', () => {
        this.scene.start(this.nextScene, this.nextData);
      });

      // Recover identity from another device
      const recoverBtn = this.add.text(W / 2 + 70, H - 55, I18n.t('name_recover') || 'I have a code', {
        fontFamily: 'system-ui, Arial', fontSize: '12px', color: '#22c55e',
      }).setOrigin(0.5).setDepth(D).setInteractive({ useHandCursor: true });
      recoverBtn.on('pointerover', () => recoverBtn.setColor('#4ade80'));
      recoverBtn.on('pointerout', () => recoverBtn.setColor('#22c55e'));
      recoverBtn.on('pointerdown', () => this._promptRecover());
    }

    // Initial preview + auto-randomize if no existing name
    if (!existing) this._randomize();
    this._updatePreview();

    this.cameras.main.fadeIn(250, 0, 0, 0);
  }

  // ── Available slots based on player progress ────────────────────────────
  _getAvailableSlots() {
    const progress = LevelStore.loadProgress();

    // Count completed worlds (unlocked level > 30 means completed)
    let worldsDone = 0;
    for (const r of REGIONS_ORDER) {
      if ((progress.unlockedByRegion?.[r] || 0) > 30) worldsDone++;
    }

    const infiniteWave = LevelStore.getInfiniteBestWave();

    return NAME_SLOTS.filter(slot => {
      if (!slot.unlock) return true;
      if (slot.unlock.type === 'worlds_completed') return worldsDone >= slot.unlock.value;
      if (slot.unlock.type === 'infinite_wave') return infiniteWave >= slot.unlock.value;
      return false;
    });
  }

  // ── Build slot pickers ──────────────────────────────────────────────────
  _buildSlots() {
    const W = this.W;
    let y = 150;

    // Group slots in display order: title/title_rare, role, creature, verb, noun, place, emoji
    const displayOrder = ['title', 'title_rare', 'role', 'creature', 'verb', 'noun', 'place', 'emoji'];

    for (const slotId of displayOrder) {
      const slot = this._availableSlots.find(s => s.id === slotId);
      const locked = !slot && NAME_SLOTS.find(s => s.id === slotId);

      // Skip title_rare if title_rare is not unlocked (show title instead)
      if (slotId === 'title_rare' && !slot) continue;
      // Skip basic title if rare is available
      if (slotId === 'title' && this._availableSlots.find(s => s.id === 'title_rare')) continue;

      if (slot) {
        this._buildSlotRow(slot, y);
        y += 48;
      } else if (locked) {
        this._buildLockedRow(locked, y);
        y += 48;
      }
    }

    this._nextY = y;
  }

  _buildSlotRow(slot, y) {
    const W = this.W;
    const labelW = 70;
    const isRequired = slot.required;

    // Label
    const labelId = slot.id === 'title_rare' ? 'title' : slot.id;
    const labelText = (I18n.t(`name_slot_${labelId}`) || labelId).toUpperCase();
    this.add.text(14, y, labelText, {
      fontFamily: 'system-ui, Arial', fontSize: '12px',
      color: isRequired ? '#fbbf24' : '#64748b',
      fontStyle: isRequired ? 'bold' : 'normal',
    }).setOrigin(0, 0.5).setDepth(D + 1);

    // Current value
    const current = this._selections[slot.id] || (isRequired ? slot.words[0] : '—');
    if (isRequired && !this._selections[slot.id]) {
      this._selections[slot.id] = slot.words[0];
    }

    const valText = this.add.text(W / 2, y, slot.id === 'emoji' ? current : current, {
      fontFamily: slot.id === 'emoji' ? 'system-ui, Arial' : 'Osaka, system-ui, Arial',
      fontSize: slot.id === 'emoji' ? '24px' : '18px',
      color: '#e2e8f0',
    }).setOrigin(0.5).setDepth(D + 1);

    // Left arrow
    const leftBtn = this.add.text(labelW + 10, y, '◀', {
      fontSize: '16px', color: '#475569',
    }).setOrigin(0.5).setDepth(D + 1).setInteractive({ useHandCursor: true });

    // Right arrow (shifted left on creature slot to make room for face)
    const rightX = slot.id === 'creature' ? W - 80 : W - 24;
    const rightBtn = this.add.text(rightX, y, '▶', {
      fontSize: '16px', color: '#475569',
    }).setOrigin(0.5).setDepth(D + 1).setInteractive({ useHandCursor: true });

    // Creature face preview (only for creature slot)
    let faceImg = null;
    if (slot.id === 'creature') {
      const faceData = CREATURE_FACES[current];
      if (faceData && this.textures.exists(faceData.key)) {
        faceImg = this.add.image(W - 54, y, faceData.key)
          .setDisplaySize(36, 36).setDepth(D + 2);
      }
    }

    const cycle = (dir) => {
      const words = slot.required ? slot.words : ['—', ...slot.words];
      const curr = this._selections[slot.id] || '—';
      let idx = words.indexOf(curr);
      idx = (idx + dir + words.length) % words.length;
      const pick = words[idx];
      this._selections[slot.id] = pick === '—' ? null : pick;
      valText.setText(pick);
      this._updatePreview();

      // Update creature face preview
      if (slot.id === 'creature' && faceImg) {
        const fd = CREATURE_FACES[pick];
        if (fd && this.textures.exists(fd.key)) {
          faceImg.setTexture(fd.key).setVisible(true);
        } else {
          faceImg.setVisible(false);
        }
      }
    };

    leftBtn.on('pointerdown', () => cycle(-1));
    rightBtn.on('pointerdown', () => cycle(1));

    // Separator line
    this.add.rectangle(W / 2, y + 22, W - 28, 1, SUBTLE, 0.5).setDepth(D);

    this._slotObjects.push({ slot, valText });
  }

  _buildLockedRow(slot, y) {
    const W = this.W;
    const labelId = slot.id === 'title_rare' ? 'title' : slot.id;
    const labelText = (I18n.t(`name_slot_${labelId}`) || labelId).toUpperCase();

    this.add.text(14, y, labelText, {
      fontFamily: 'system-ui, Arial', fontSize: '12px', color: '#1e293b',
    }).setOrigin(0, 0.5).setDepth(D + 1);

    // Lock icon + unlock hint
    let hint = '🔒 ';
    if (slot.unlock?.type === 'worlds_completed') {
      hint += (I18n.t('name_unlock_worlds', { n: slot.unlock.value }) || `Complete ${slot.unlock.value} world${slot.unlock.value > 1 ? 's' : ''}`);
    } else if (slot.unlock?.type === 'infinite_wave') {
      hint += (I18n.t('name_unlock_wave', { n: slot.unlock.value }) || `Reach Infinite wave ${slot.unlock.value}`);
    }

    this.add.text(W / 2, y, hint, {
      fontFamily: 'system-ui, Arial', fontSize: '12px', color: '#1e293b',
    }).setOrigin(0.5).setDepth(D + 1);

    this.add.rectangle(W / 2, y + 22, W - 28, 1, SUBTLE, 0.3).setDepth(D);
  }

  // ── Year picker ────────────────────────────────────────────────────────
  _buildYearPicker() {
    const W = this.W;
    const y = this._nextY;

    this.add.text(14, y, (I18n.t('name_slot_year') || 'YEAR').toUpperCase(), {
      fontFamily: 'system-ui, Arial', fontSize: '10px', color: '#fbbf24', fontStyle: 'bold',
    }).setOrigin(0, 0.5).setDepth(D + 1);

    this._yearText = this.add.text(W / 2, y, this._year || '—', {
      fontFamily: 'Osaka, system-ui, Arial', fontSize: '16px', color: '#e2e8f0',
    }).setOrigin(0.5).setDepth(D + 1)
      .setInteractive({ useHandCursor: true });

    this._yearText.on('pointerdown', () => this._promptYear());

    this.add.rectangle(W / 2, y + 22, W - 28, 1, SUBTLE, 0.5).setDepth(D);
    this._nextY = y + 48;
  }

  // ── Preview ────────────────────────────────────────────────────────────
  _updatePreview() {
    const name = buildDisplayName(this._selections, this._year, this._flag);
    this._previewText.setText(name || '...');
  }

  // ── Randomize ──────────────────────────────────────────────────────────
  _randomize() {
    for (const slot of this._availableSlots) {
      const pick = slot.words[Math.floor(Math.random() * slot.words.length)];
      this._selections[slot.id] = slot.required ? pick : (Math.random() > 0.3 ? pick : null);
    }
    // Always fill required
    for (const slot of this._availableSlots) {
      if (slot.required && !this._selections[slot.id]) {
        this._selections[slot.id] = slot.words[0];
      }
    }

    // Update all slot displays
    for (const { slot, valText } of this._slotObjects) {
      valText.setText(this._selections[slot.id] || '—');
    }

    this._updatePreview();
  }

  // ── Confirm + save + share ─────────────────────────────────────────────
  async _confirm() {
    // Validate required slots
    for (const slot of this._availableSlots) {
      if (slot.required && !this._selections[slot.id]) {
        return; // can't confirm without required fields
      }
    }

    const lbName = buildLeaderboardName(this._selections, this._year);

    const identity = {
      selections: { ...this._selections },
      year: this._year,
      countryCode: this._countryCode,
      discord: this._discordValue || null,
      linkCode: localStorage.getItem('pzm_link_code') || null,
      createdAt: new Date().toISOString(),
    };

    // Save locally
    localStorage.setItem(STORAGE_KEY, JSON.stringify(identity));
    if (this._discordValue) localStorage.setItem(DISCORD_KEY, this._discordValue);
    localStorage.setItem('pzm_player_name', lbName);
    localStorage.setItem('pzm_country', this._countryCode);

    // Sync to Supabase (fire and don't block)
    const cloudResult = await saveIdentity({
      displayName: lbName,
      selections: identity.selections,
      year: identity.year,
      countryCode: identity.countryCode,
      discord: identity.discord,
      linkCode: identity.linkCode,
    });

    if (cloudResult.ok) {
      identity.linkCode = cloudResult.linkCode;
      localStorage.setItem('pzm_link_code', cloudResult.linkCode);
      // Update local storage with link code
      localStorage.setItem(STORAGE_KEY, JSON.stringify(identity));
    }

    // Show share card before proceeding
    this._showShareCard(identity);
  }

  // ── Share card overlay ─────────────────────────────────────────────────
  _showShareCard(identity) {
    const W = this.W;
    const H = this.H;

    // Full-screen themed background
    if (this.textures.exists('name-bg')) {
      const bg = this.add.image(W / 2, H / 2, 'name-bg').setDepth(900);
      const scaleX = W / bg.width;
      const scaleY = H / bg.height;
      const scale = Math.max(scaleX, scaleY);
      bg.setScale(scale);
    } else {
      this.add.rectangle(W / 2, H / 2, W, H, 0x0a0e1a, 1).setDepth(900);
    }

    // Dark overlay for readability
    this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.55).setDepth(901);

    // Card panel (centered, translucent)
    const cardH = 280;
    const cardY = H / 2 - 20;
    this.add.rectangle(W / 2, cardY, W - 32, cardH, 0x0d1117, 0.85)
      .setStrokeStyle(2, GOLD, 0.6).setDepth(902);

    // "I am" label
    this.add.text(W / 2, cardY - 95, I18n.t('name_iam') || 'I AM', {
      fontFamily: 'system-ui, Arial', fontSize: '14px', color: '#94a3b8',
    }).setOrigin(0.5).setDepth(903);

    // Full display name
    const displayName = buildDisplayName(identity.selections, identity.year, toFlag(identity.countryCode));
    this.add.text(W / 2, cardY - 48, displayName, {
      fontFamily: 'Osaka, system-ui, Arial', fontSize: '18px',
      color: '#fbbf24', wordWrap: { width: W - 80 },
    }).setOrigin(0.5).setDepth(903);

    // "in PAZURUMON"
    this.add.text(W / 2, cardY + 5, 'in PAZURUMON', {
      fontFamily: 'Osaka, system-ui, Arial', fontSize: '24px',
      color: '#ffffff', stroke: '#0b0f14', strokeThickness: 4,
    }).setOrigin(0.5).setDepth(903);

    // Discord if provided
    let infoY = cardY + 42;
    if (identity.discord) {
      this.add.text(W / 2, infoY, `Discord: ${identity.discord}`, {
        fontFamily: 'system-ui, Arial', fontSize: '13px', color: '#7289da',
      }).setOrigin(0.5).setDepth(903);
      infoY += 22;
    }

    // Link code (cross-device recovery)
    if (identity.linkCode) {
      this.add.text(W / 2, infoY, `${I18n.t('name_link_code') || 'YOUR CODE'}: ${identity.linkCode}`, {
        fontFamily: 'Osaka, system-ui, Arial', fontSize: '16px',
        color: '#22c55e', stroke: '#0b0f14', strokeThickness: 2,
      }).setOrigin(0.5).setDepth(903);

      this.add.text(W / 2, infoY + 20, I18n.t('name_link_hint') || 'Save this code to recover your identity on any device', {
        fontFamily: 'system-ui, Arial', fontSize: '10px', color: '#475569',
        wordWrap: { width: W - 80 },
      }).setOrigin(0.5).setDepth(903);
    }

    // Share button
    const shareY = cardY + 90;
    const shareBtn = this.add.rectangle(W / 2, shareY, 200, 38, 0x0f2a4a, 0.95)
      .setStrokeStyle(1.5, 0x3b82f6, 0.7).setDepth(903)
      .setInteractive({ useHandCursor: true });
    this.add.text(W / 2, shareY, I18n.t('name_share') || '📤  SHARE', {
      fontFamily: 'system-ui, Arial', fontSize: '13px', color: '#60a5fa', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(904);
    shareBtn.on('pointerdown', () => this._shareIdentity(identity));

    // ── VISIT US AT PAZZURUMON.COM ───────────────────────────────────────
    this.add.text(W / 2, H - 48, 'PAZZURUMON.COM', {
      fontFamily: 'Osaka, system-ui, Arial', fontSize: '16px',
      color: '#fbbf24', stroke: '#0b0f14', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(903).setAlpha(0.9);

    this.add.text(W / 2, H - 28, I18n.t('name_cta') || 'Leaderboards, News & Community', {
      fontFamily: 'system-ui, Arial', fontSize: '11px', color: '#94a3b8',
    }).setOrigin(0.5).setDepth(903).setAlpha(0.8);

    // Continue button (below share card)
    const contY = cardY + cardH / 2 + 35;
    const contBtn = this.add.text(W / 2, contY, I18n.t('name_continue') || 'CONTINUE ▶', {
      fontFamily: 'Osaka, system-ui, Arial', fontSize: '14px', color: '#fbbf24',
    }).setOrigin(0.5).setDepth(903).setInteractive({ useHandCursor: true });
    contBtn.on('pointerdown', () => {
      this.cameras.main.fadeOut(200, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start(this.nextScene, this.nextData);
      });
    });
  }

  // ── Share screenshot ───────────────────────────────────────────────────
  async _shareIdentity(identity) {
    const displayName = buildDisplayName(identity.selections, identity.year, toFlag(identity.countryCode));
    const shareText = `${I18n.t('name_iam') || 'I am'} ${displayName} in PAZURUMON! 🎮🔥`;

    // Try canvas screenshot
    this.game.renderer.snapshot(async (image) => {
      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(image, 0, 0);

      const blob = await new Promise(r => canvas.toBlob(r, 'image/png'));
      if (!blob) return;

      const file = new File([blob], 'pazurumon-identity.png', { type: 'image/png' });

      if (navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({ title: 'PAZURUMON', text: shareText, files: [file] });
          return;
        } catch (_) { /* cancelled */ }
      }

      // Fallback: download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'pazurumon-identity.png';
      a.click();
      URL.revokeObjectURL(url);
    });
  }

  // ── Year prompt (HTML overlay) ─────────────────────────────────────────
  _promptYear() {
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.85);z-index:9999;display:flex;align-items:center;justify-content:center;';
    overlay.innerHTML = `
      <div style="background:#0d1117;border:1px solid #fbbf24;border-radius:8px;
                  padding:24px 20px;min-width:280px;text-align:center;font-family:system-ui,Arial;">
        <div style="color:#e2e8f0;font-size:14px;margin-bottom:12px;">${I18n.t('name_year_prompt') || 'Year of birth (2 digits)'}</div>
        <input id="yr-input" type="number" min="0" max="99" maxlength="2" value="${this._year}"
          style="width:80px;padding:9px;background:#1e293b;border:1px solid #475569;
                 color:#fbbf24;font-size:24px;border-radius:4px;text-align:center;outline:none;font-family:Osaka,system-ui,Arial;"/>
        <div style="display:flex;gap:10px;margin-top:14px;">
          <button id="yr-cancel" style="flex:1;padding:9px;background:#1f2937;border:1px solid #334155;color:#94a3b8;cursor:pointer;border-radius:4px;font-size:13px;">${I18n.t('prompt_cancel') || 'CANCEL'}</button>
          <button id="yr-ok" style="flex:2;padding:9px;background:#1e3a5f;border:1px solid #fbbf24;color:#fbbf24;cursor:pointer;border-radius:4px;font-size:13px;font-weight:bold;">OK</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);

    const input = overlay.querySelector('#yr-input');
    input.focus(); input.select();

    const confirm = () => {
      let val = input.value.trim();
      if (val.length > 2) val = val.slice(-2);
      document.body.removeChild(overlay);
      this._year = val || '';
      this._yearText.setText(this._year || '—');
      this._updatePreview();
    };
    const cancel = () => { document.body.removeChild(overlay); };

    overlay.querySelector('#yr-ok').addEventListener('click', confirm);
    overlay.querySelector('#yr-cancel').addEventListener('click', cancel);
    input.addEventListener('keydown', e => {
      if (e.key === 'Enter') confirm();
      if (e.key === 'Escape') cancel();
    });
  }

  // ── Discord prompt (HTML overlay) ──────────────────────────────────────
  _promptDiscord() {
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.85);z-index:9999;display:flex;align-items:center;justify-content:center;';
    overlay.innerHTML = `
      <div style="background:#0d1117;border:1px solid #5865f2;border-radius:8px;
                  padding:24px 20px;min-width:300px;text-align:center;font-family:system-ui,Arial;">
        <div style="color:#e2e8f0;font-size:14px;margin-bottom:4px;">Discord</div>
        <div style="color:#475569;font-size:11px;margin-bottom:12px;">${I18n.t('name_discord_hint') || 'Optional — join the Pazurumon community!'}</div>
        <input id="dc-input" maxlength="40" placeholder="@username" value="${(this._discordValue || '').replace(/"/g, '')}"
          style="width:100%;padding:9px 10px;background:#1e293b;border:1px solid #5865f2;
                 color:#7289da;font-size:14px;border-radius:4px;box-sizing:border-box;outline:none;"/>
        <div style="display:flex;gap:10px;margin-top:14px;">
          <button id="dc-cancel" style="flex:1;padding:9px;background:#1f2937;border:1px solid #334155;color:#94a3b8;cursor:pointer;border-radius:4px;font-size:13px;">${I18n.t('prompt_cancel') || 'CANCEL'}</button>
          <button id="dc-ok" style="flex:2;padding:9px;background:#2d3250;border:1px solid #5865f2;color:#5865f2;cursor:pointer;border-radius:4px;font-size:13px;font-weight:bold;">OK</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);

    const input = overlay.querySelector('#dc-input');
    input.focus(); input.select();

    const confirm = () => {
      const val = input.value.trim().slice(0, 40);
      document.body.removeChild(overlay);
      this._discordValue = val;
      this._discordLabel.setText(val || (I18n.t('name_discord_tap') || 'tap to add @username'));
      this._discordLabel.setColor(val ? '#7289da' : '#334155');
    };
    const cancel = () => { document.body.removeChild(overlay); };

    overlay.querySelector('#dc-ok').addEventListener('click', confirm);
    overlay.querySelector('#dc-cancel').addEventListener('click', cancel);
    input.addEventListener('keydown', e => {
      if (e.key === 'Enter') confirm();
      if (e.key === 'Escape') cancel();
    });
  }

  // ── Persistence ────────────────────────────────────────────────────────
  // ── Recover identity prompt (HTML overlay) ──────────────────────────
  _promptRecover() {
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.85);z-index:9999;display:flex;align-items:center;justify-content:center;';
    overlay.innerHTML = `
      <div style="background:#0d1117;border:1px solid #22c55e;border-radius:8px;
                  padding:24px 20px;min-width:300px;text-align:center;font-family:system-ui,Arial;">
        <div style="color:#e2e8f0;font-size:14px;margin-bottom:4px;font-weight:bold;">${I18n.t('name_recover_title') || 'Recover Identity'}</div>
        <div style="color:#475569;font-size:11px;margin-bottom:14px;">${I18n.t('name_recover_hint') || 'Enter your 6-character code'}</div>
        <input id="rc-input" maxlength="6" placeholder="ABC123"
          style="width:140px;padding:12px;background:#1e293b;border:1px solid #22c55e;
                 color:#22c55e;font-size:22px;border-radius:4px;text-align:center;
                 outline:none;font-family:Osaka,system-ui,Arial;letter-spacing:4px;text-transform:uppercase;"/>
        <div id="rc-status" style="color:#ef4444;font-size:11px;margin-top:8px;min-height:16px;"></div>
        <div style="display:flex;gap:10px;margin-top:10px;">
          <button id="rc-cancel" style="flex:1;padding:9px;background:#1f2937;border:1px solid #334155;color:#94a3b8;cursor:pointer;border-radius:4px;font-size:13px;">${I18n.t('prompt_cancel') || 'CANCEL'}</button>
          <button id="rc-ok" style="flex:2;padding:9px;background:#0f2a1a;border:1px solid #22c55e;color:#22c55e;cursor:pointer;border-radius:4px;font-size:13px;font-weight:bold;">${I18n.t('name_recover_btn') || 'RECOVER'}</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);

    const input  = overlay.querySelector('#rc-input');
    const status = overlay.querySelector('#rc-status');
    input.focus();

    const doRecover = async () => {
      const code = input.value.trim().toUpperCase();
      if (code.length !== 6) {
        status.textContent = I18n.t('name_recover_invalid') || 'Code must be 6 characters';
        return;
      }
      status.style.color = '#fbbf24';
      status.textContent = I18n.t('name_recover_loading') || 'Searching...';

      const result = await recoverIdentity(code);

      if (result.ok) {
        status.style.color = '#22c55e';
        status.textContent = I18n.t('name_recover_success') || 'Identity recovered!';
        setTimeout(() => {
          document.body.removeChild(overlay);
          // Restart scene with recovered data
          this.scene.restart({ nextScene: this.nextScene, nextData: this.nextData, editMode: true });
        }, 1200);
      } else if (result.error === 'not_found') {
        status.style.color = '#ef4444';
        status.textContent = I18n.t('name_recover_not_found') || 'Code not found';
      } else {
        status.style.color = '#ef4444';
        status.textContent = I18n.t('name_recover_error') || 'Connection error — try again';
      }
    };

    const cancel = () => { document.body.removeChild(overlay); };

    overlay.querySelector('#rc-ok').addEventListener('click', doRecover);
    overlay.querySelector('#rc-cancel').addEventListener('click', cancel);
    input.addEventListener('keydown', e => {
      if (e.key === 'Enter') doRecover();
      if (e.key === 'Escape') cancel();
    });
  }

  _loadIdentity() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }

  static hasIdentity() {
    return !!localStorage.getItem(STORAGE_KEY);
  }
}
